import {
  bowlerIdentity,
  moneyInTotals,
  onSquadBowlers,
  tournamentMoneyTotals,
  walkUpEntryCount,
} from "./directorMoney";
import { dashboardCounts, signupMoney } from "./signupDashboard";
import { getSetupUrl } from "./adminNavigation";
import { yearFinance } from "./financeYear";
import { listTournamentStatus } from "./tournamentProgress";
import { tournamentPath } from "./tournamentUrls";
import { addCurrency } from "../utils/currencyUtils";
import { todayISODate } from "../utils/homepageSeason";
import { seasonAnchor } from "./squadSpan";

export const calendarYearOf = (value) => {
  if (!value) return null;
  const match = String(value).match(/^(\d{4})-/);
  if (match) return Number(match[1]);
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.getFullYear();
};

/** Season that starts August 1 of the returned year and ends the next July 31. */
export const fiscalYearOf = (value) => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-/);
  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]);
    return month >= 8 ? year : year - 1;
  }
  const parsed = new Date(value);
  if (!value || Number.isNaN(parsed.getTime())) return null;
  const year = parsed.getFullYear();
  return parsed.getMonth() + 1 >= 8 ? year : year - 1;
};

export const currentFiscalYear = (now = new Date()) => {
  const year = now.getFullYear();
  return now.getMonth() + 1 >= 8 ? year : year - 1;
};

export const fiscalYearLabel = (startYear) => {
  const start = Number(startYear);
  if (!start) return "";
  return `${start}-${String(start + 1).slice(-2)}`;
};

export const tournamentYears = (tournaments, now = new Date()) => {
  const years = new Set([currentFiscalYear(now)]);
  (tournaments || []).forEach((tournament) => {
    const year = fiscalYearOf(seasonAnchor(tournament));
    if (year) years.add(year);
  });
  return [...years].sort((a, b) => b - a);
};

const isoDate = (value) => {
  const match = String(value || "").match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
};

export const isoToday = (now = new Date()) => {
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
};

const squadDates = (tournament) =>
  (tournament?.squads || [])
    .filter((squad) => squad && !squad.isDeleted && squad.squadDate)
    .map((squad) => isoDate(squad.squadDate))
    .filter(Boolean)
    .sort();

const squadsForStatus = (tournament) => {
  if (Array.isArray(tournament?.squads) && tournament.squads.length > 0) {
    return tournament.squads;
  }
  return (tournament?.squadDates || []).map((squadDate) => ({
    squadDate,
    isDeleted: false,
  }));
};

/** Same labels as the Tournaments list. */
export const yearListStatus = (tournament, today = todayISODate()) =>
  listTournamentStatus(
    {
      startDate: tournament?.startDate || tournament?.start,
      squads: squadsForStatus(tournament),
    },
    {
      resultsVerified: Boolean(tournament?.resultsVerified),
      allowSignups: tournament?.allowSignups !== false,
      today,
    }
  );

const hrefForListStatus = (tournament, status) => {
  const key = { slug: tournament.slug, id: tournament.id };
  if (status === "Tournament Setup") return getSetupUrl(key);
  if (status === "Standings Available") {
    return tournamentPath(key, "/standings");
  }
  return tournamentPath(key);
};

export const yearRollup = (groups = [], today = todayISODate()) => {
  const identities = new Set();
  let entries = 0;
  let onlineCollected = 0;
  let deskCollected = 0;
  let entryFees = 0;
  let sidePots = 0;
  let brackets = 0;
  let walkUpEntries = null;
  let onlinePayments = 0;
  let deskPayments = 0;
  const tournaments = (groups || []).map((group) => {
    const totals = tournamentMoneyTotals(group.signups);
    const signups = group.signups || [];
    const onlinePaymentCount = signups.filter(
      (signup) => signupMoney(signup) === "paid"
    ).length;
    const deskPaymentCount = signups.filter(
      (signup) => signupMoney(signup) === "walk_up"
    ).length;
    onlinePayments += onlinePaymentCount;
    deskPayments += deskPaymentCount;
    const walkUps =
      group.teams == null ? null : walkUpEntryCount(group.teams, signups);
    if (walkUps != null) walkUpEntries = (walkUpEntries || 0) + walkUps;
    const incoming = moneyInTotals(group.signups);
    const counts = dashboardCounts(group.signups);
    const dates = squadDates(group.tournament);
    const start = dates[0] || isoDate(group.tournament?.startDate);
    entries += totals.entries;
    onlineCollected = addCurrency(onlineCollected, totals.onlineCollected);
    deskCollected = addCurrency(deskCollected, totals.deskCollected);
    entryFees = addCurrency(entryFees, incoming.entryFees);
    sidePots = addCurrency(sidePots, incoming.sidePots);
    brackets = addCurrency(brackets, incoming.brackets);
    onSquadBowlers(group.signups).forEach((bowler) => {
      const key = bowlerIdentity(bowler);
      if (key) identities.add(key);
    });
    const hasSquads = dates.length > 0;
    const listStatus = yearListStatus(group.tournament || {}, today);
    return {
      id: group.tournament?.id,
      name: group.tournament?.name || "",
      slug: group.tournament?.slug || "",
      startDate: group.tournament?.startDate || "",
      start,
      end: dates[dates.length - 1] || start,
      hasSquads,
      entries: totals.entries,
      walkUpEntries: walkUps,
      onlinePaymentCount,
      deskPaymentCount,
      onlineCollected: totals.onlineCollected,
      deskCollected: totals.deskCollected,
      totalCollected: addCurrency(totals.onlineCollected, totals.deskCollected),
      moneyIn: incoming,
      dueAtDesk: totals.dueAtDesk,
      paidCount: counts.paid,
      dueCount: counts.due,
      waitlistCount: counts.waitlist,
      listStatus,
      href: hrefForListStatus(group.tournament || {}, listStatus),
    };
  });

  return {
    tournamentCount: tournaments.length,
    entries,
    walkUpEntries,
    onlinePayments,
    deskPayments,
    uniqueBowlers: identities.size,
    onlineCollected,
    deskCollected,
    totalCollected: addCurrency(onlineCollected, deskCollected),
    moneyIn: {
      entryFees,
      sidePots,
      brackets,
      total: addCurrency(entryFees, sidePots, brackets),
    },
    tournaments,
  };
};

export const emptyYearRollup = () => yearRollup([]);

/** Non-zero season attention. Empty when nothing is due or waiting. */
export const yearAttention = (rollup) => {
  if (!rollup) return [];
  const items = [];
  const dueCount = Number(rollup.dueCount) || 0;
  const dueAmount = Number(rollup.dueAtDesk) || 0;
  if (dueCount > 0 || dueAmount > 0) {
    items.push({
      key: "due",
      label: "Still due",
      count: dueCount,
      amount: dueAmount,
    });
  }
  const waitlist = (rollup.tournaments || []).reduce(
    (sum, tournament) => sum + (Number(tournament.waitlistCount) || 0),
    0
  );
  if (waitlist > 0) {
    items.push({ key: "waitlist", label: "Waitlist", count: waitlist });
  }
  return items;
};

const byStart = (a, b) => String(a.start || "").localeCompare(String(b.start || ""));

export const yearBoard = (tournaments = [], today = isoToday()) => {
  const current = [];
  const past = [];

  (tournaments || []).forEach((tournament) => {
    const end = tournament.end || tournament.start;
    const start = tournament.start;
    const phase =
      !end || end < today ? "past" : start > today ? "upcoming" : "in_progress";
    const row = { ...tournament, phase };
    if (phase === "past") past.push(row);
    else current.push(row);
  });

  current.sort(byStart);
  past.sort(byStart);
  return { current, past };
};

export const loadOrganizationYear = async ({
  tournaments,
  year,
  fetchSignups,
  teamsByTournamentId = null,
}) => {
  const inYear = (tournaments || []).filter(
    (tournament) => fiscalYearOf(seasonAnchor(tournament)) === year
  );
  const groups = await Promise.all(
    inYear.map(async (tournament) => ({
      tournament,
      signups: await fetchSignups(tournament.id),
      teams:
        teamsByTournamentId == null
          ? null
          : teamsByTournamentId[tournament.id] || [],
    }))
  );
  return yearRollup(groups);
};

const asNumber = (value) => Number(value) || 0;

const financeFromRpc = (row) => {
  const finance = row?.finance;
  if (!finance?.usable) return null;
  const locked = Boolean(finance.locked);
  return {
    id: row.id,
    name: row.name || "",
    startDate: row.startDate || "",
    lineage: asNumber(finance.lineage),
    prizeFund: asNumber(finance.prizeFund),
    prizeFundFees: asNumber(finance.prizeFundFees),
    added: asNumber(finance.added),
    expenses: asNumber(finance.expenses),
    otherFees: (finance.otherFees || []).map((fee) => ({
      name: fee.name,
      amount: asNumber(fee.amount),
    })),
    lineageLabel: "Linage",
    usable: true,
    locked,
    lockedPayouts: locked ? asNumber(finance.lockedPayouts) : null,
  };
};

/**
 * Year page shape from get_organization_year.
 * Money in and money out still balance in tournamentStatement.
 */
export const organizationYearFromRpc = (payload, today = todayISODate()) => {
  let entries = 0;
  let onlineCollected = 0;
  let deskCollected = 0;
  let entryFees = 0;
  let sidePots = 0;
  let brackets = 0;
  let walkUpEntries = 0;
  let walkUpCollected = 0;
  let onlinePayments = 0;
  let deskPayments = 0;
  let dueAtDesk = 0;
  let dueCount = 0;
  let refunds = 0;
  let refundCount = 0;

  const tournaments = (payload?.tournaments || []).map((row) => {
    const dates = (row.squadDates || [])
      .map((date) => isoDate(date))
      .filter(Boolean)
      .sort();
    const start = dates[0] || isoDate(row.startDate);
    const dueEntries = asNumber(row.dueCount);
    const waitlistCount = asNumber(row.waitlistCount);
    const hasSquads = dates.length > 0;
    const listStatus = yearListStatus(
      {
        startDate: row.startDate,
        squadDates: dates,
        resultsVerified: row.resultsVerified,
        allowSignups: row.allowSignups,
      },
      today
    );
    const online = asNumber(row.onlineCollected);
    const desk = asNumber(row.deskCollected);
    const walkUps = asNumber(row.walkUpEntries);
    const walkUpAmount = asNumber(row.walkUpCollected);
    const onlinePaymentCount = asNumber(row.onlinePayments);
    const deskPaymentCount = asNumber(row.deskPayments);
    const due = asNumber(row.dueAtDesk);
    const refundAmount = asNumber(row.refunds);
    const refundEntries = asNumber(row.refundCount);
    const incoming = {
      entryFees: asNumber(row.entryFees),
      sidePots: asNumber(row.sidePots),
      brackets: asNumber(row.brackets),
      bracketPrize: row.bracketPrize == null ? null : asNumber(row.bracketPrize),
      bracketFees: row.bracketFees == null ? null : asNumber(row.bracketFees),
      onlineCollected: online,
      deskCollected: desk,
      walkUpCollected: walkUpAmount,
      dueAtDesk: due,
      refunds: refundAmount,
    };
    incoming.total = addCurrency(
      incoming.entryFees,
      incoming.sidePots,
      incoming.brackets
    );
    entries += asNumber(row.entries);
    onlinePayments += onlinePaymentCount;
    deskPayments += deskPaymentCount;
    dueAtDesk = addCurrency(dueAtDesk, due);
    dueCount += dueEntries;
    refunds = addCurrency(refunds, refundAmount);
    refundCount += refundEntries;
    walkUpEntries += walkUps;
    walkUpCollected = addCurrency(walkUpCollected, walkUpAmount);
    onlineCollected = addCurrency(onlineCollected, online);
    deskCollected = addCurrency(deskCollected, desk);
    entryFees = addCurrency(entryFees, incoming.entryFees);
    sidePots = addCurrency(sidePots, incoming.sidePots);
    brackets = addCurrency(brackets, incoming.brackets);
    return {
      id: row.id,
      name: row.name || "",
      slug: row.slug || "",
      startDate: row.startDate || "",
      start,
      end: dates[dates.length - 1] || start,
      hasSquads,
      entries: asNumber(row.entries),
      walkUpEntries: walkUps,
      walkUpCollected: walkUpAmount,
      onlinePaymentCount,
      deskPaymentCount,
      onlineCollected: online,
      deskCollected: desk,
      totalCollected: addCurrency(online, desk),
      moneyIn: incoming,
      dueAtDesk: due,
      refunds: refundAmount,
      refundCount: refundEntries,
      paidCount: asNumber(row.paidCount),
      dueCount: dueEntries,
      waitlistCount,
      listStatus,
      href: hrefForListStatus(row, listStatus),
    };
  });

  return {
    rollup: {
      tournamentCount: tournaments.length,
      entries,
      walkUpEntries,
      walkUpCollected,
      onlinePayments,
      deskPayments,
      uniqueBowlers: asNumber(payload?.uniqueBowlers),
      onlineCollected,
      deskCollected,
      totalCollected: addCurrency(onlineCollected, deskCollected),
      dueAtDesk,
      dueCount,
      refunds,
      refundCount,
      moneyIn: {
        entryFees,
        sidePots,
        brackets,
        total: addCurrency(entryFees, sidePots, brackets),
      },
      tournaments,
    },
    finance: yearFinance(
      (payload?.tournaments || []).map(financeFromRpc).filter(Boolean)
    ),
  };
};
